import { useState } from "react";
import { Check } from "lucide-react";

import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

interface PricingPlansProps {
    onSelectPlan: (planKey: string) => void;
    isLoading?: boolean;
    showCta?: boolean;
    ctaText?: string;
    currentPlanKey?: string | null;
    hideMarketing?: boolean;
}

export default function PricingPlans({
    onSelectPlan,
    isLoading = false,
    showCta = true,
    ctaText = "Subscribe",
    currentPlanKey = null,
    hideMarketing = false,
}: PricingPlansProps) {
    // Default to yearly if current plan is yearly, otherwise monthly
    const defaultInterval = currentPlanKey?.includes("_yearly") ? "yearly" : "monthly";
    const [billingInterval, setBillingInterval] = useState<"monthly" | "yearly">(defaultInterval);

    const plans = [
        {
            key: "usage_only",
            name: "Pay As You Go",
            price: "$1",
            period: "/month",
            yearlyPrice: null, // No yearly option
            description: "Try it out, pay only for what you use",
            monthlyCredit: null,
            yearlyCredit: null,
            features: [
                "Unlimited podcasts",
                "Chat with any podcast via RSS",
                "$0.01 per question",
                "$0.10 per transcription minute",
                "No commitment",
            ],
            planKeyMonthly: "usage_only_monthly",
            planKeyYearly: null,
            popular: false,
        },
        {
            key: "basic",
            name: "Basic",
            price: "$5",
            period: "/month",
            yearlyPrice: "$42",
            yearlyPeriod: "/year",
            description: "Best value for regular listeners",
            monthlyCredit: "$2",
            yearlyCredit: "$20",
            features: [
                "Unlimited podcasts",
                "Chat with any podcast via RSS",
                "Email support",
                "Same low rates after credit",
            ],
            planKeyMonthly: "basic_monthly",
            planKeyYearly: "basic_yearly",
            popular: true,
        },
        {
            key: "premium",
            name: "Premium",
            price: "$7",
            period: "/month",
            yearlyPrice: "$60",
            yearlyPeriod: "/year",
            description: "For heavy users—more credits, priority support",
            monthlyCredit: "$4",
            yearlyCredit: "$30",
            features: [
                "Unlimited podcasts",
                "Chat with any podcast via RSS",
                "Auto email summaries for new episodes",
                "Priority support",
                "Same low rates after credit",
            ],
            planKeyMonthly: "premium_monthly",
            planKeyYearly: "premium_yearly",
            popular: false,
            exclusive: "📬 Email Digests",
        },
    ];

    return (
        <div className="w-full max-w-7xl mx-auto">
            {/* Billing Toggle */}
            <div className="flex justify-center mb-12">
                <div className={cn(
                    "flex items-center p-1 rounded-full",
                    "bg-gray-200 dark:bg-gray-800",
                    theme.borders.neubrutalism,
                    theme.shadows.neubrutalismSm
                )}>
                    <button
                        onClick={() => setBillingInterval("monthly")}
                        className={cn(
                            "px-6 py-2 rounded-full text-sm font-bold transition-all",
                            billingInterval === "monthly"
                                ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                                : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        )}
                    >
                        Monthly
                    </button>
                    <button
                        onClick={() => setBillingInterval("yearly")}
                        className={cn(
                            "px-6 py-2 rounded-full text-sm font-bold transition-all",
                            billingInterval === "yearly"
                                ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                                : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        )}
                    >
                        Yearly <span className="ml-1 text-xs text-green-600 dark:text-green-400 font-normal">(Save ~30%)</span>
                    </button>
                </div>
            </div>

            {/* Pricing Cards */}
            <div className="grid md:grid-cols-3 gap-8">
                {plans.map((plan) => {
                    const isYearly = billingInterval === "yearly";
                    // Skip plans that don't have a yearly option if yearly is selected, 
                    // OR show them as monthly-only? 
                    // The requirement said "Default it to monthly but let the user use a 'switch' to change to annual."
                    // Usage only is monthly only. Let's show it but force monthly price or disable?
                    // Usually better to just show the monthly price with a note, or disable the toggle for that card.
                    // For simplicity, if yearly is selected and plan has no yearly, we can either hide it or show monthly.
                    // Let's show monthly for usage-only even in yearly view, but maybe grayed out or just explicit.

                    const price = isYearly && plan.yearlyPrice ? plan.yearlyPrice : plan.price;
                    const period = isYearly && plan.yearlyPrice ? plan.yearlyPeriod : plan.period;
                    const planKey = isYearly && plan.planKeyYearly ? plan.planKeyYearly : plan.planKeyMonthly;
                    const isMonthlyOnly = isYearly && !plan.planKeyYearly;
                    const isCurrentPlan = planKey === currentPlanKey;
                    
                    // Build features list with appropriate credit amount
                    const creditAmount = isYearly && plan.yearlyCredit ? plan.yearlyCredit : plan.monthlyCredit;
                    const creditPeriod = isYearly ? "yearly" : "monthly";
                    const featuresList = creditAmount 
                        ? [`${creditAmount} ${creditPeriod} credit included`, ...plan.features]
                        : plan.features;

                    return (
                        <div
                            key={plan.key}
                            className={cn(
                                "relative p-8 rounded-2xl flex flex-col",
                                isCurrentPlan 
                                    ? theme.colors.primaryOrange 
                                    : plan.popular && !hideMarketing 
                                        ? theme.colors.primaryOrange 
                                        : "bg-white dark:bg-gray-800",
                                theme.borders.neubrutalism,
                                isCurrentPlan || (plan.popular && !hideMarketing)
                                    ? theme.shadows.neubrutalismLg 
                                    : theme.shadows.neubrutalism,
                                "hover:-translate-y-1 transition-transform",
                                isCurrentPlan && "ring-4 ring-primary-orange/50 dark:ring-primary-orange-dark/50"
                            )}
                        >
                            {isCurrentPlan && (
                                <div className={cn(
                                    "absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-sm font-bold",
                                    "bg-gray-900 dark:bg-white text-white dark:text-gray-900",
                                    theme.borders.neubrutalism,
                                    theme.shadows.neubrutalismSm
                                )}>
                                    CURRENT PLAN
                                </div>
                            )}
                            {plan.popular && !hideMarketing && !isCurrentPlan && (
                                <div className={cn(
                                    "absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-sm font-bold",
                                    "bg-gray-900 dark:bg-white text-white dark:text-gray-900",
                                    theme.borders.neubrutalism,
                                    theme.shadows.neubrutalismSm
                                )}>
                                    MOST POPULAR
                                </div>
                            )}

                            <div className="mb-6">
                                <h3 className={cn(
                                    "text-2xl font-black mb-2",
                                    isCurrentPlan || (plan.popular && !hideMarketing) 
                                        ? "text-gray-900 dark:text-gray-900" 
                                        : "text-gray-900 dark:text-white"
                                )}>
                                    {plan.name}
                                </h3>
                                <div className="flex items-baseline mb-3">
                                    <span className={cn(
                                        "text-5xl font-black",
                                        isCurrentPlan || (plan.popular && !hideMarketing)
                                            ? "text-gray-900 dark:text-gray-900" 
                                            : "text-gray-900 dark:text-white"
                                    )}>
                                        {price}
                                    </span>
                                    {period && (
                                        <span className={cn(
                                            "ml-2",
                                            isCurrentPlan || (plan.popular && !hideMarketing)
                                                ? "text-gray-800 dark:text-gray-800" 
                                                : "text-gray-600 dark:text-gray-400"
                                        )}>
                                            {period}
                                        </span>
                                    )}
                                </div>
                                {isMonthlyOnly && isYearly && (
                                    <p className={cn(
                                        "text-xs mb-2 font-medium",
                                        isCurrentPlan || (plan.popular && !hideMarketing)
                                            ? "text-gray-800 dark:text-gray-800" 
                                            : "text-gray-500 dark:text-gray-400"
                                    )}>
                                        (Monthly only)
                                    </p>
                                )}
                                <p className={cn(
                                    isCurrentPlan || (plan.popular && !hideMarketing)
                                        ? "text-gray-900 dark:text-gray-900 font-medium" 
                                        : "text-gray-700 dark:text-gray-300"
                                )}>
                                    {plan.description}
                                </p>
                                {plan.exclusive && !hideMarketing && (
                                    <div className={cn(
                                        "mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold",
                                        "bg-gradient-to-r from-violet-500 to-fuchsia-500",
                                        "text-white border-2 border-violet-600 dark:border-violet-400"
                                    )}>
                                        <span>{plan.exclusive}</span>
                                    </div>
                                )}
                            </div>

                            <ul className="space-y-3 mb-8 flex-1">
                                {featuresList.map((feature) => (
                                    <li key={feature} className="flex items-start gap-3">
                                        <div className={cn(
                                            "mt-0.5 p-0.5 rounded-full",
                                            isCurrentPlan || (plan.popular && !hideMarketing)
                                                ? "bg-gray-900 dark:bg-white"
                                                : theme.colors.primaryOrange
                                        )}>
                                            <Check className={cn(
                                                "w-4 h-4",
                                                isCurrentPlan || (plan.popular && !hideMarketing)
                                                    ? "text-white dark:text-gray-900"
                                                    : "text-gray-900"
                                            )} />
                                        </div>
                                        <span className={cn(
                                            "text-sm",
                                            isCurrentPlan || (plan.popular && !hideMarketing)
                                                ? "text-gray-900 dark:text-gray-900 font-medium" 
                                                : "text-gray-700 dark:text-gray-300"
                                        )}>
                                            {feature}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            {showCta && (
                                <button
                                    onClick={() => planKey && !isCurrentPlan && onSelectPlan(planKey)}
                                    disabled={isLoading || !planKey || isCurrentPlan}
                                    className={cn(
                                        "w-full py-3 font-semibold rounded-xl",
                                        isCurrentPlan
                                            ? "bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 border-3 border-gray-400 dark:border-gray-500 cursor-not-allowed"
                                            : plan.popular && !hideMarketing
                                                ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-3 border-gray-900 dark:border-white shadow-neubrutalism dark:shadow-neubrutalism-dark hover:-translate-y-0.5 transition-transform"
                                                : theme.buttons.primary,
                                        "disabled:opacity-50 disabled:cursor-not-allowed"
                                    )}
                                >
                                    {isCurrentPlan 
                                        ? "Current Plan" 
                                        : isLoading 
                                            ? "Processing..." 
                                            : ctaText}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
